import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Search,
  FolderOpen,
  Users,
  Lock,
  Clock,
  MessageSquare,
  FileText,
  Settings,
  MoreHorizontal,
  Trash2,
  Edit3,
  Share2,
  Star,
  StarOff,
  BarChart3,
  TrendingUp,
  Newspaper,
  PenLine,
  Briefcase,
  Code,
  Globe,
  Zap,
  Target,
  BookOpen,
  Layers,
  Database
} from 'lucide-react';
import { useProjectStore, MOCK_PROJECTS } from '../../../stores/projectStore';

// Icon mapping
const ICON_MAP = {
  folder: FolderOpen,
  chart: BarChart3,
  trend: TrendingUp,
  news: Newspaper,
  pen: PenLine,
  business: Briefcase,
  code: Code,
  globe: Globe,
  zap: Zap,
  target: Target,
  search: Search,
  chat: MessageSquare,
  book: BookOpen,
  layers: Layers,
  database: Database,
  settings: Settings,
};

const ICON_COLORS = {
  folder: 'text-blue-400',
  chart: 'text-emerald-400',
  trend: 'text-green-400',
  news: 'text-amber-400',
  pen: 'text-orange-400',
  business: 'text-slate-400',
  code: 'text-violet-400',
  globe: 'text-cyan-400',
  zap: 'text-yellow-400',
  target: 'text-red-400',
  search: 'text-indigo-400',
  chat: 'text-pink-400',
  book: 'text-teal-400',
  layers: 'text-purple-400',
  database: 'text-sky-400',
  settings: 'text-gray-400',
};

// Project card component
const ProjectCard = ({ project, onClick, onEdit, onDelete }) => {
  const [showMenu, setShowMenu] = useState(false);
  const isOwner = project.ownerId === 'user_current';
  const isShared = project.sharedWith?.length > 0;

  const getShareLabel = () => {
    if (!isShared) return 'Private';
    if (project.sharedWith.includes('all')) return 'Shared with all';
    if (project.sharedWith.length === 1 && project.sharedWith[0].includes('부')) {
      return `Shared with ${project.sharedWith[0]}`;
    }
    return `Shared with ${project.sharedWith.length} people`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return `${Math.floor(diffDays / 30)}mo ago`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      whileHover={{ y: -4, transition: { duration: 0.2 } }}
      onClick={() => onClick(project.id)}
      className="bg-bg-200/60 rounded-2xl p-5 cursor-pointer transition-all group relative"
      style={{ border: '1px solid hsl(var(--border-300) / 0.12)' }}
    >
      {/* Menu button */}
      {isOwner && (
        <div className="absolute top-4 right-4 z-10">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
            className="p-1.5 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-bg-300/50 transition-all"
          >
            <MoreHorizontal className="w-4 h-4 text-text-400" />
          </button>

          {/* Dropdown menu */}
          <AnimatePresence>
            {showMenu && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="absolute right-0 top-8 bg-bg-100 rounded-xl shadow-xl overflow-hidden min-w-[140px] z-20"
                style={{ border: '1px solid hsl(var(--border-300) / 0.15)' }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => {
                    onEdit(project);
                    setShowMenu(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-text-200 hover:bg-bg-200 transition-colors"
                >
                  <Edit3 className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={() => {
                    onDelete(project.id);
                    setShowMenu(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Icon & title */}
      <div className="flex items-start gap-3 mb-4">
        {(() => {
          const IconComponent = ICON_MAP[project.icon] || FolderOpen;
          const iconColor = ICON_COLORS[project.icon] || 'text-blue-400';
          return (
            <div className="w-12 h-12 rounded-xl bg-bg-300/50 flex items-center justify-center">
              <IconComponent className={`w-6 h-6 ${iconColor}`} />
            </div>
          );
        })()}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-text-100 group-hover:text-sedaily-400 transition-colors truncate">
            @{project.name}
          </h3>
          {project.description && (
            <p className="text-xs text-text-400 mt-0.5 line-clamp-2">
              {project.description}
            </p>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 mb-4 text-xs text-text-400">
        <span className="flex items-center gap-1">
          <MessageSquare className="w-3.5 h-3.5" />
          {project.conversations?.length || 0} conversations
        </span>
        <span className="flex items-center gap-1">
          <FileText className="w-3.5 h-3.5" />
          {project.files?.length || 0} files
        </span>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-3" style={{ borderTop: '1px solid hsl(var(--border-300) / 0.08)' }}>
        <div className="flex items-center gap-2">
          {isShared ? (
            <span className="flex items-center gap-1 text-xs text-text-400">
              <Users className="w-3.5 h-3.5" />
              {getShareLabel()}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-text-500">
              <Lock className="w-3.5 h-3.5" />
              Private
            </span>
          )}
        </div>
        <span className="flex items-center gap-1 text-xs text-text-500">
          <Clock className="w-3.5 h-3.5" />
          {formatDate(project.updatedAt)}
        </span>
      </div>
    </motion.div>
  );
};

// Filter tabs
const FilterTabs = ({ activeFilter, onFilterChange }) => {
  const filters = [
    { id: 'all', label: 'All', icon: FolderOpen },
    { id: 'mine', label: 'My Projects', icon: Star },
    { id: 'shared', label: 'Shared', icon: Users },
  ];

  return (
    <div className="flex items-center gap-1 bg-bg-200/30 rounded-lg p-0.5">
      {filters.map((filter) => {
        const Icon = filter.icon;
        const isActive = activeFilter === filter.id;
        return (
          <button
            key={filter.id}
            onClick={() => onFilterChange(filter.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              isActive
                ? 'bg-bg-100 text-text-100 shadow-sm'
                : 'text-text-400 hover:text-text-200'
            }`}
          >
            <Icon className="w-4 h-4" />
            {filter.label}
          </button>
        );
      })}
    </div>
  );
};

// Main component
const ProjectList = () => {
  const navigate = useNavigate();
  const {
    projects,
    setProjects,
    isLoading,
    setLoading,
    filter,
    setFilter,
    searchQuery,
    setSearchQuery,
    getFilteredProjects,
    addProject,
    deleteProject
  } = useProjectStore();

  // Initial data load (Mock)
  useEffect(() => {
    setLoading(true);
    // Mock data load (replace with API call)
    setTimeout(() => {
      setProjects(MOCK_PROJECTS);
      setLoading(false);
    }, 500);
  }, [setProjects, setLoading]);

  const filteredProjects = getFilteredProjects();

  const handleProjectClick = (projectId) => {
    navigate(`/projects/${projectId}`);
  };

  // Create new project and navigate directly to it
  const handleCreateNewProject = () => {
    const newProject = {
      id: `proj_${Date.now()}`,
      name: 'New Project',
      description: '',
      icon: 'folder',
      instructions: '',
      sharedWith: [],
      ownerId: 'user_current',
      ownerName: 'Me',
      conversations: [],
      files: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    addProject(newProject);
    navigate(`/projects/${newProject.id}`);
  };

  const handleEditProject = (project) => {
    navigate(`/projects/${project.id}`);
  };

  const handleDeleteProject = (projectId) => {
    if (confirm('Are you sure you want to delete this project?')) {
      deleteProject(projectId);
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-text-100">Projects</h1>
            <p className="text-text-400 text-sm mt-1">
              Create AI projects to collaborate with your team
            </p>
          </div>
          <button
            onClick={handleCreateNewProject}
            className="flex items-center gap-2 px-4 py-2.5 bg-sedaily-500 hover:bg-sedaily-600 text-white rounded-xl font-medium transition-colors"
          >
            <Plus className="w-5 h-5" />
            New project
          </button>
        </div>

        {/* Search & filter */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-400" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-bg-200 text-text-100 text-sm pl-10 pr-4 py-2.5 rounded-xl outline-none focus:ring-2 focus:ring-sedaily-500/50 transition-colors placeholder:text-text-400 border border-bg-300/50"
            />
          </div>

          <div className="flex items-center gap-3">
            <FilterTabs activeFilter={filter} onFilterChange={setFilter} />
            <span className="text-sm text-text-500">
              Sort by <span className="text-text-300">Activity</span>
            </span>
          </div>
        </div>

        {/* Project grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-sedaily-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 rounded-2xl bg-bg-200/50 flex items-center justify-center mx-auto mb-4">
              <FolderOpen className="w-10 h-10 text-text-400" />
            </div>
            <h3 className="text-lg font-semibold text-text-200 mb-2">
              {searchQuery ? 'No results found' : 'No projects yet'}
            </h3>
            <p className="text-text-400 text-sm mb-6">
              {searchQuery
                ? 'Try a different search term'
                : 'Create a new project to start collaborating with AI'}
            </p>
            {!searchQuery && (
              <button
                onClick={handleCreateNewProject}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-sedaily-500 hover:bg-sedaily-600 text-white rounded-xl font-medium transition-colors"
              >
                <Plus className="w-5 h-5" />
                Create First Project
              </button>
            )}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            <AnimatePresence mode="popLayout">
              {filteredProjects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  onClick={handleProjectClick}
                  onEdit={handleEditProject}
                  onDelete={handleDeleteProject}
                />
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>

    </div>
  );
};

export default ProjectList;
